import { Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Divider, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProfile } from '../../services/profile.js';
import { impactAnalysisApi, type ImpactAnalysisData } from '../../services/impactAnalysis.js';
import { LogoImage } from '../../components/LogoImage.js';

export const ProfileLegacyDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [profile, setProfile] = React.useState<any>(null);
  const [impactLoading, setImpactLoading] = React.useState(false);
  const [impactError, setImpactError] = React.useState<string | null>(null);
  const [impact, setImpact] = React.useState<ImpactAnalysisData | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = await getProfile({ idOrTicker: id, useMock: true });
        setProfile(p);
      } catch (e: any) {
        setError(e?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Fetch impact analysis when profile is available
  const fetchImpact = React.useCallback(async (reason?: string) => {
    if (!profile?.id) return;
    try {
      setImpactError(null);
      setImpactLoading(true);
      const data = await impactAnalysisApi.getImpactAnalysis(profile.id, { reason });
      setImpact(data);
    } catch (e: any) {
      setImpactError(e?.message || 'Failed to load impact analysis');
    } finally {
      setImpactLoading(false);
    }
  }, [profile?.id]);

  React.useEffect(() => {
    if (profile?.id) {
      fetchImpact('profile-detail-initial');
    }
  }, [profile?.id, fetchImpact]);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Card>
        <CardHeader title="Profile" subheader={id} />
        <Divider />
        <CardContent>
          {loading && (
            <Stack spacing={1}>
              <Skeleton width={240} />
              <Skeleton width={480} />
            </Stack>
          )}
          {error && (
            <Typography color="error">{error}</Typography>
          )}
          {profile && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LogoImage
                  src={profile.logoUrl}
                  alt={profile.ticker}
                  size={32}
                  paddingPx={2}
                  fallbackText={(profile.ticker || profile.name || '?')}
                />
                <Typography variant="h6">{profile.name} ({profile.ticker})</Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {profile.sector && <Chip label={profile.sector} size="small" />}
                {profile.industry && <Chip label={profile.industry} size="small" />}
                <Chip label={`Research ${Math.round(profile.researchCompleteness || 0)}%`} size="small" />
              </Stack>
              <Typography variant="body2" color="text.secondary">{profile.description}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={() => navigate(`/profiles/${encodeURIComponent(profile.id)}/preview`)}>Open Preview</Button>
                <Button variant="contained" onClick={() => navigate(`/canvas?profileId=${encodeURIComponent(profile.id)}`)}>Add to Canvas (Module)</Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Impact Analysis */}
      <Card>
        <CardHeader
          title="Impact Analysis"
          subheader={impact?.lastUpdated ? `Last updated ${new Date(impact.lastUpdated).toLocaleString()}` : undefined}
          action={
            <Button size="small" onClick={() => fetchImpact('profile-detail-manual-refresh')} disabled={!profile?.id || impactLoading}>
              {impactLoading ? <CircularProgress size={16} /> : 'Refresh'}
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {!impact && impactLoading && (
            <Stack spacing={1}>
              <Skeleton width={220} />
              <Skeleton width={420} />
              <Skeleton width={360} />
            </Stack>
          )}
          {impactError && (
            <Typography color="error">{impactError}</Typography>
          )}
          {impact && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`Sentiment: ${impact.marketSentiment}`} color={impact.marketSentiment === 'bullish' ? 'success' : impact.marketSentiment === 'bearish' ? 'error' : 'default'} />
                <Typography variant="body2" color="text.secondary">{impact.summary}</Typography>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                {impact.impacts.map((imp, idx) => (
                  <Box key={`${imp.title}-${idx}`} sx={{ p: 1, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>{imp.title}</Typography>
                      <Chip size="small" variant="outlined" label={imp.type} />
                      <Chip size="small" color={imp.impact === 'high' ? 'error' : imp.impact === 'medium' ? 'warning' : 'default'} label={`Impact: ${imp.impact}`} />
                      <Chip size="small" variant="outlined" label={`Conf: ${Math.round(imp.confidence * 100)}%`} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{imp.description}</Typography>
                    {imp.affectedStocks && imp.affectedStocks.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {imp.affectedStocks.map((s) => (
                          <Chip key={s} size="small" label={s} variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
